import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Clock, AlertCircle, MessageSquare } from 'lucide-react';
import GlassCard from '../../../components/shared/GlassCard';

const ApprovalWorkflows = () => {
  const [approvals, setApprovals] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [selectedApproval, setSelectedApproval] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [comment, setComment] = useState('');
  const [actionType, setActionType] = useState(null); // 'approve' or 'reject'

  useEffect(() => {
    fetchApprovals();
    fetchStats();
  }, [filterStatus, filterType]);

  const fetchApprovals = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const params = new URLSearchParams();
      if (filterStatus) params.append('status', filterStatus);
      if (filterType) params.append('request_type', filterType);
      
      const response = await fetch(`${backendUrl}/api/rbac/approvals?${params}`, {
        headers: {
          'x-user-role': localStorage.getItem('userRole') || 'super_admin',
          'x-user-id': localStorage.getItem('userId') || 'system'
        }
      });
      const data = await response.json();
      setApprovals(data.approvals || []);
    } catch (error) {
      console.error('Error fetching approvals:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/rbac/approvals/stats/summary`, {
        headers: {
          'x-user-role': localStorage.getItem('userRole') || 'super_admin'
        }
      });
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleApprove = async () => {
    if (!selectedApproval) return;
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/rbac/approvals/${selectedApproval.id}/approve`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-role': localStorage.getItem('userRole') || 'super_admin',
          'x-user-id': localStorage.getItem('userId') || 'system',
          'x-user-email': localStorage.getItem('userEmail') || 'system@urgaa.in',
          'x-user-name': localStorage.getItem('userName') || 'System'
        },
        body: JSON.stringify({ comment })
      });
      
      if (response.ok) {
        setShowDetailModal(false);
        setComment('');
        fetchApprovals();
        fetchStats();
        alert('Approved successfully!');
      } else {
        const data = await response.json();
        alert(data.detail || 'Failed to approve');
      }
    } catch (error) {
      console.error('Error approving:', error);
      alert('Error approving request');
    }
  };

  const handleReject = async () => {
    if (!selectedApproval || !comment.trim()) {
      alert('Comment is required for rejection');
      return;
    }
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/rbac/approvals/${selectedApproval.id}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-role': localStorage.getItem('userRole') || 'super_admin',
          'x-user-id': localStorage.getItem('userId') || 'system',
          'x-user-email': localStorage.getItem('userEmail') || 'system@urgaa.in',
          'x-user-name': localStorage.getItem('userName') || 'System'
        },
        body: JSON.stringify({ comment })
      });
      
      if (response.ok) {
        setShowDetailModal(false);
        setComment('');
        fetchApprovals();
        fetchStats();
        alert('Rejected successfully!');
      } else {
        const data = await response.json();
        alert(data.detail || 'Failed to reject');
      }
    } catch (error) {
      console.error('Error rejecting:', error);
      alert('Error rejecting request');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: '#F39C12',
      approved: '#0DA34E',
      rejected: '#E74C3C',
      cancelled: '#95A5A6'
    };
    return colors[status] || '#7F8C8D';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return Clock;
      case 'approved': return CheckCircle;
      case 'rejected': return XCircle;
      default: return AlertCircle;
    }
  };

  const formatTimestamp = (timestamp) => {
    try {
      return new Date(timestamp).toLocaleString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric',
        hour: '2-digit', 
        minute: '2-digit'
      });
    } catch {
      return timestamp;
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2" style={{ color: 'var(--text)' }}>
          Approval Workflows
        </h1>
        <p className="text-sm" style={{ color: 'var(--muted)' }}>
          Multi-level approval requests for governance, DSAT, and station deployments
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--panel-bg)', border: '1px solid var(--panel-border)' }}>
            <div className="text-2xl font-bold" style={{ color: 'var(--text)' }}>{stats.total}</div>
            <div className="text-sm" style={{ color: 'var(--muted)' }}>Total Requests</div>
          </div>
          <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--panel-bg)', border: '1px solid var(--panel-border)' }}>
            <div className="text-2xl font-bold" style={{ color: '#F39C12' }}>{stats.pending}</div>
            <div className="text-sm" style={{ color: 'var(--muted)' }}>Pending</div>
          </div>
          <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--panel-bg)', border: '1px solid var(--panel-border)' }}>
            <div className="text-2xl font-bold" style={{ color: '#0DA34E' }}>{stats.approved}</div>
            <div className="text-sm" style={{ color: 'var(--muted)' }}>Approved</div>
          </div>
          <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--panel-bg)', border: '1px solid var(--panel-border)' }}>
            <div className="text-2xl font-bold" style={{ color: '#E74C3C' }}>{stats.rejected}</div>
            <div className="text-sm" style={{ color: 'var(--muted)' }}>Rejected</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 flex gap-4">
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 rounded-lg border"
          style={{ 
            backgroundColor: 'var(--panel-bg)',
            borderColor: 'var(--panel-border)',
            color: 'var(--text)'
          }}
        >
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>

        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-4 py-2 rounded-lg border"
          style={{ 
            backgroundColor: 'var(--panel-bg)',
            borderColor: 'var(--panel-border)',
            color: 'var(--text)'
          }}
        >
          <option value="">All Types</option>
          <option value="governance_note">Governance Note</option>
          <option value="dsat_target">DSAT Target</option>
          <option value="station_deployment">Station Deployment</option>
        </select>
      </div>

      {/* Approvals List */}
      <GlassCard>
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-gray-300 border-t-transparent"></div>
            <p className="mt-2 text-sm" style={{ color: 'var(--muted)' }}>Loading approvals...</p>
          </div>
        ) : approvals.length === 0 ? (
          <div className="text-center py-12">
            <Clock className="w-12 h-12 mx-auto mb-4 opacity-40" style={{ color: 'var(--muted)' }} />
            <p className="text-lg font-medium mb-2" style={{ color: 'var(--text)' }}>No approval requests found</p>
            <p className="text-sm" style={{ color: 'var(--muted)' }}>
              {filterStatus || filterType ? 'Try adjusting your filters' : 'Approval requests will appear here'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {approvals.map((approval) => {
              const StatusIcon = getStatusIcon(approval.final_status);
              return (
                <div
                  key={approval.id}
                  className="p-4 rounded-lg border cursor-pointer hover:shadow-md transition-shadow"
                  style={{ borderColor: 'var(--panel-border)' }}
                  onClick={() => {
                    setSelectedApproval(approval);
                    setShowDetailModal(true);
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span 
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium"
                          style={{ 
                            backgroundColor: `${getStatusColor(approval.final_status)}15`,
                            color: getStatusColor(approval.final_status)
                          }}
                        >
                          <StatusIcon className="w-3 h-3" />
                          {approval.final_status}
                        </span>
                        <span className="text-sm font-mono" style={{ color: 'var(--muted)' }}>
                          {approval.request_type.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <div className="text-sm mb-1" style={{ color: 'var(--text)' }}>
                        Requested by: <strong>{approval.requested_by_name}</strong>
                      </div>
                      <div className="text-xs" style={{ color: 'var(--muted)' }}>
                        {formatTimestamp(approval.requested_at)}
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <div className="text-xs" style={{ color: 'var(--muted)' }}>
                          Level {approval.current_level} of {approval.approval_chain.length}
                        </div>
                        {approval.comments.length > 0 && (
                          <div className="flex items-center gap-1 text-xs" style={{ color: 'var(--muted)' }}>
                            <MessageSquare className="w-3 h-3" />
                            {approval.comments.length} comments
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </GlassCard>

      {/* Detail Modal */}
      {showDetailModal && selectedApproval && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-2xl font-bold mb-4" style={{ color: 'var(--text)' }}>
                Approval Request Details
              </h3>

              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm font-medium" style={{ color: 'var(--muted)' }}>Type</div>
                    <div style={{ color: 'var(--text)' }}>{selectedApproval.request_type.replace(/_/g, ' ')}</div>
                  </div>
                  <div>
                    <div className="text-sm font-medium" style={{ color: 'var(--muted)' }}>Status</div>
                    <div style={{ color: getStatusColor(selectedApproval.final_status) }}>
                      {selectedApproval.final_status}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm font-medium" style={{ color: 'var(--muted)' }}>Requested By</div>
                    <div style={{ color: 'var(--text)' }}>{selectedApproval.requested_by_name}</div>
                  </div>
                  <div>
                    <div className="text-sm font-medium" style={{ color: 'var(--muted)' }}>Requested At</div>
                    <div style={{ color: 'var(--text)' }}>{formatTimestamp(selectedApproval.requested_at)}</div>
                  </div>
                </div>

                {/* Approval Chain */}
                <div>
                  <div className="text-sm font-medium mb-2" style={{ color: 'var(--text)' }}>Approval Chain</div>
                  <div className="space-y-2">
                    {selectedApproval.approval_chain.map((level) => (
                      <div 
                        key={level.level}
                        className="flex items-center gap-2 p-2 rounded"
                        style={{ backgroundColor: 'var(--panel-bg)' }}
                      >
                        <div className="text-sm font-medium" style={{ color: 'var(--text)' }}>
                          Level {level.level}
                        </div>
                        <div className="text-xs" style={{ color: 'var(--muted)' }}>
                          {level.approver_role.replace(/_/g, ' ')}
                        </div>
                        <div className="ml-auto">
                          <span 
                            className="px-2 py-1 rounded text-xs"
                            style={{ 
                              backgroundColor: `${getStatusColor(level.status)}15`,
                              color: getStatusColor(level.status)
                            }}
                          >
                            {level.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Comments */}
                {selectedApproval.comments.length > 0 && (
                  <div>
                    <div className="text-sm font-medium mb-2" style={{ color: 'var(--text)' }}>Comments</div>
                    <div className="space-y-2">
                      {selectedApproval.comments.map((c, idx) => (
                        <div key={idx} className="p-2 rounded" style={{ backgroundColor: 'var(--panel-bg)' }}>
                          <div className="text-xs font-medium" style={{ color: 'var(--text)' }}>{c.user}</div>
                          <div className="text-sm" style={{ color: 'var(--text)' }}>{c.comment}</div>
                          <div className="text-xs" style={{ color: 'var(--muted)' }}>{formatTimestamp(c.timestamp)}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Section (only if pending) */}
                {selectedApproval.final_status === 'pending' && (
                  <div>
                    <div className="text-sm font-medium mb-2" style={{ color: 'var(--text)' }}>Add Comment (Optional)</div>
                    <textarea
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border"
                      style={{ 
                        backgroundColor: 'var(--panel-bg)',
                        borderColor: 'var(--panel-border)',
                        color: 'var(--text)'
                      }}
                      rows={3}
                      placeholder="Add a comment..."
                    />
                  </div>
                )}
              </div>

              <div className="flex gap-2 mt-6">
                <button
                  onClick={() => {
                    setShowDetailModal(false);
                    setComment('');
                  }}
                  className="flex-1 px-4 py-2 rounded-lg border font-medium"
                  style={{ 
                    borderColor: 'var(--panel-border)',
                    color: 'var(--text)'
                  }}
                >
                  Close
                </button>
                {selectedApproval.final_status === 'pending' && (
                  <>
                    <button
                      onClick={handleReject}
                      className="flex-1 px-4 py-2 rounded-lg font-medium"
                      style={{ 
                        backgroundColor: '#E74C3C',
                        color: 'white'
                      }}
                    >
                      Reject
                    </button>
                    <button
                      onClick={handleApprove}
                      className="flex-1 px-4 py-2 rounded-lg font-medium"
                      style={{ 
                        backgroundColor: 'var(--g4g-accent)',
                        color: 'white'
                      }}
                    >
                      Approve
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApprovalWorkflows;
